import { useMutation } from "@apollo/client";
import Label from "@mui/icons-material/Label";
import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ManageTagsFormDialog } from "components/ManageTags/ManageTagsFormDialog";
import { WorkflowTagPickerAdapter } from "components/ManageTags/WorkflowTagPickerAdapter";
import { EntityUpdate } from "components/ManageTags/types";
import { gql } from "generated-graphql";
import pluralize from "pluralize";
import { useCallback, useState, useMemo } from "react";
import { useBulkActionContext } from "./BulkActionsMenu";

// GraphQL mutation for updating workflow tags in bulk
export const UPDATE_WORKFLOW_TAGS_BULK_MUTATION = gql(`
  mutation UpdateWorkflowTagsBulk($workflowUpdates: [WorkflowTagUpdateInput!]!) {
    updateWorkflowTagsBulk(workflowUpdates: $workflowUpdates) {
      id
      tags {
        id
        name
      }
    }
  }
`);

// Common workflow tags
const COMMON_WORKFLOW_TAGS = ["compliance", "engineering", "linear"];
const LOCAL_STORAGE_KEY = "workflow-recent-tags";

// Menu item component
export function ManageWorkflowTags() {
  const { setDialogState, selectedRows } = useBulkActionContext();

  return (
    <MenuItem
      onClick={() => setDialogState("manageWorkflowTags")}
      disabled={selectedRows.length === 0}
    >
      <ListItemIcon>
        <Label />
      </ListItemIcon>
      <ListItemText>Manage Tags</ListItemText>
    </MenuItem>
  );
}

// Dialog component
export function ManageWorkflowTagsDialog() {
  const alerts = useAlerts();
  const { dialogState, onClose, selectedRows, clearSelection } =
    useBulkActionContext();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [updateWorkflowTags, { loading }] = useMutation(
    UPDATE_WORKFLOW_TAGS_BULK_MUTATION,
    {
      refetchQueries: ["WorkflowActivities"],
    }
  );

  // Transform rows to fit ManageTagsFormDialog interface
  const transformedRows: Array<{ id: string; tags?: string[] }> = useMemo(
    () =>
      selectedRows
        .filter((row) => row.workflowId)
        .map((row) => ({
          id: row.workflowId,
          tags: row.tags || [],
        })),
    [selectedRows]
  );

  const getEntityId = useCallback((row: { id: string }) => row.id, []);

  const handleSubmit = useCallback(
    async (updates: EntityUpdate[]) => {
      try {
        // Transform updates to the format expected by updateWorkflowTagsBulk
        const workflowUpdates = updates.map((update) => ({
          workflowId: update.id,
          tagNames: update.tags,
        }));

        if (workflowUpdates.length > 0) {
          await updateWorkflowTags({
            variables: {
              workflowUpdates,
            },
          });
        }

        const workflowCount = workflowUpdates.length;
        alerts.success(
          `Successfully updated tags for ${workflowCount} ${pluralize(
            "workflow",
            workflowCount
          )}`
        );

        clearSelection();
        onClose();
      } catch (err) {
        alerts.error("An error occurred while updating the workflow tags", err);
      }
    },
    [updateWorkflowTags, alerts, onClose, clearSelection]
  );

  const handleClose = useCallback(() => {
    setShowConfirmation(false);
    onClose();
  }, [onClose]);

  const confirm = useCallback(() => {
    setShowConfirmation(true);
  }, []);

  const onCloseConfirmation = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  return (
    <ManageTagsFormDialog
      selectedRows={transformedRows}
      getEntityId={getEntityId}
      onClose={handleClose}
      onCloseConfirmation={onCloseConfirmation}
      confirm={confirm}
      open={dialogState === "manageWorkflowTags"}
      withConfirmation={true}
      showConfirmation={showConfirmation}
      onSubmit={handleSubmit}
      loading={loading}
      entityName="Workflow"
      localStorageKey={LOCAL_STORAGE_KEY}
      commonTags={COMMON_WORKFLOW_TAGS}
      TagPickerComponent={WorkflowTagPickerAdapter}
    />
  );
}